import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import locations from '../api/locations/hooks'
import { ENTITY_TYPE } from '../common/constants'
import {
    selectLocationCityFilter,
    selectLocationCountryFilter,
    selectLocationCountyFilter,
    selectLocationIslandFilter,
    selectLocationQuarterFilter,
    selectLocationWiderAreaFilter,
} from '../store/formFilters'
import { enableFormValues } from '../utils/formUtils/forms'

export const useLocationValues = (type) => {
    const locationFilters = useSelector((state) => ({
        country: selectLocationCountryFilter(state),
        county: selectLocationCountyFilter(state),
        city: selectLocationCityFilter(state),
        widerArea: selectLocationWiderAreaFilter(state),
        quarter: selectLocationQuarterFilter(state),
        island: selectLocationIslandFilter(state),
    }))

    const locationFieldFilters = useMemo(() => {
        return {
            county: { country: locationFilters.country },
            city: { county: locationFilters.county },
            widerArea: { city: locationFilters.city },
            quarter: { widerArea: locationFilters.widerArea },
            island: { country: locationFilters.country },
        }
    }, [locationFilters])

    const locationModules = [ENTITY_TYPE.PROJECT, ENTITY_TYPE.PROPERTY, ENTITY_TYPE.CONTACT, ENTITY_TYPE.COMPANY]

    const formValuesQueries = {
        fields: {
            static: ['country'],
            dynamic: {
                county: ['county'],
                city: ['city'],
                widerArea: ['widerArea'],
                quarter: ['quarter'],
                island: ['island'],
            },
        },
        enabled: {
            static: enableFormValues([...locationModules, ENTITY_TYPE.ADMINISTRATION], type),
            dynamic: {
                county: enableFormValues([...locationModules, ENTITY_TYPE.ADMINISTRATION], type, locationFilters.country),
                city: enableFormValues([...locationModules, ENTITY_TYPE.ADMINISTRATION], type, locationFilters.county),
                widerArea: enableFormValues(locationModules, type, locationFilters.city),
                quarter: enableFormValues(locationModules, type, locationFilters.widerArea),
                island: enableFormValues(locationModules, type, locationFilters.country),
            },
        },
    }

    const { data: countryValues, isPending: isCountryPending } = locations.FormValues.useQuery(
        formValuesQueries.fields.static,
        undefined,
        formValuesQueries.enabled.static
    )
    const { data: countyValues, isPending: isCountyPending } = locations.FormValues.useQuery(
        formValuesQueries.fields.dynamic.county,
        locationFieldFilters.county,
        formValuesQueries.enabled.dynamic.county
    )
    const { data: cityValues, isPending: isCityPending } = locations.FormValues.useQuery(
        formValuesQueries.fields.dynamic.city,
        locationFieldFilters.city,
        formValuesQueries.enabled.dynamic.city
    )
    const { data: widerAreaValues, isPending: isWiderAreaPending } = locations.FormValues.useQuery(
        formValuesQueries.fields.dynamic.widerArea,
        locationFieldFilters.widerArea,
        formValuesQueries.enabled.dynamic.widerArea
    )
    const { data: quarterValues, isPending: isQuarterPending } = locations.FormValues.useQuery(
        formValuesQueries.fields.dynamic.quarter,
        locationFieldFilters.quarter,
        formValuesQueries.enabled.dynamic.quarter
    )
    const { data: islandValues, isPending: isIslandPending } = locations.FormValues.useQuery(
        formValuesQueries.fields.dynamic.island,
        locationFieldFilters.island,
        formValuesQueries.enabled.dynamic.island
    )

    const locationValues = {
        ...countryValues,
        ...countyValues,
        ...cityValues,
        ...widerAreaValues,
        ...quarterValues,
        ...islandValues,
    }

    // a disabled query (enabled: false) reports isPending: true forever, so only count it while it's actually enabled
    const isPending =
        (formValuesQueries.enabled.static.enabled && isCountryPending) ||
        (formValuesQueries.enabled.dynamic.county.enabled && isCountyPending) ||
        (formValuesQueries.enabled.dynamic.city.enabled && isCityPending) ||
        (formValuesQueries.enabled.dynamic.widerArea.enabled && isWiderAreaPending) ||
        (formValuesQueries.enabled.dynamic.quarter.enabled && isQuarterPending) ||
        (formValuesQueries.enabled.dynamic.island.enabled && isIslandPending)

    return { locationValues, isPending }
}
